import React, { useEffect, useMemo } from 'react';

import { useGetAudioConversationDetailQuery } from '@/services/api/audioConversationService';
import { useGetTranscriptSegmentsQuery } from '@/services/api/transcriptService';

import { TranscriptEvidencePane } from './TranscriptEvidencePane';

type ConversationLike = {
  id: string;
  name?: string;
  start_timestamp?: string;
  created_at?: string;
  source?: string;
  recording_url?: string | null;
};

interface TranscriptSectionProps {
  matterId: string;
  conversation: ConversationLike;
  focusSegmentId?: string | null;
  onSegmentsSet?: (segments: any[]) => void;
}

const formatDateTime = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

export const TranscriptSection: React.FC<TranscriptSectionProps> = ({
  matterId,
  conversation,
  focusSegmentId,
  onSegmentsSet,
}) => {
  const conversationId = conversation.id;

  const { data: convoDetail } = useGetAudioConversationDetailQuery({
    matterId,
    conversationId,
  });

  const { data: segments = [] } = useGetTranscriptSegmentsQuery({
    matterId,
    conversationId,
  });

  useEffect(() => {
    if (onSegmentsSet) onSegmentsSet(segments as any[]);
  }, [segments, onSegmentsSet]);

  const minimalConversation = useMemo(() => {
    const cd = (convoDetail || {}) as any;
    return {
      id: conversation.id,
      name: 'Bland Call',
      duration: cd?.duration || null,
      start_timestamp:
        conversation.start_timestamp || conversation.created_at || '',
      is_inbound: Boolean((conversation as any)?.is_inbound),
      audio_url:
        cd?.transcript_artifact?.audio_url ||
        cd?.audio_url ||
        conversation.recording_url ||
        null,
      audio_path: cd?.audio_path || null,
    };
  }, [convoDetail, conversation]);

  return (
    <div className="bg-white border rounded overflow-hidden">
      <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
        <div className="text-xs font-semibold text-gray-700">Bland Call</div>
        <div className="text-xs text-gray-500">
          {formatDateTime(
            conversation.start_timestamp || conversation.created_at,
          )}
        </div>
      </div>
      <div className="h-[360px] overflow-hidden">
        <TranscriptEvidencePane
          conversation={minimalConversation as any}
          segments={segments as any}
          focusSegmentId={focusSegmentId || undefined}
        />
      </div>
    </div>
  );
};
